/**
 * Keeps the Cognito session alive.
 *
 * Cognito ID tokens live ~1 hour, but the SDK holds a 30-day refresh token
 * in localStorage. `tryRestoreCognitoSession()` (cognito-client.ts) calls
 * `getSession()`, which transparently exchanges the refresh token for fresh
 * ID/access tokens whenever the cached ones are expired — so "restore" and
 * "refresh" are the same operation. Before this module existed the function
 * was never called and every user was silently logged out hourly.
 *
 * Three triggers:
 *   1. Boot — survive reloads and PWA cold starts.
 *   2. Interval + visibilitychange — refresh BEFORE expiry so in-flight
 *      requests never race a dying token (margin: 10 min).
 *   3. On-demand from api.ts's 401 handler — one restore-then-retry before
 *      the session is declared dead.
 *
 * Dev IDP mode: no refresh tokens exist; everything here no-ops.
 */

import { authMode, tryRestoreCognitoSession } from './cognito-client.js';
import { useAuth } from './auth-store.js';

const REFRESH_MARGIN_MS = 10 * 60 * 1000;
const CHECK_INTERVAL_MS = 60 * 1000;

let restoreInFlight: Promise<boolean> | null = null;

/**
 * Restore/refresh the Cognito session into the auth store. Resolves true
 * when a valid session was written. Concurrent callers share one attempt.
 */
export function attemptSessionRestore(): Promise<boolean> {
  if (authMode !== 'cognito') return Promise.resolve(false);
  if (restoreInFlight) return restoreInFlight;
  restoreInFlight = (async () => {
    try {
      const session = await tryRestoreCognitoSession();
      if (!session) return false;
      useAuth.getState().setSession(session);
      return true;
    } catch {
      // Refresh token revoked/expired or network down — let the caller
      // fall through to its normal unauthenticated path.
      return false;
    } finally {
      restoreInFlight = null;
    }
  })();
  return restoreInFlight;
}

export function startSessionKeeper(): void {
  if (authMode !== 'cognito') return;

  void attemptSessionRestore();

  const tick = () => {
    const { token, expiresAt } = useAuth.getState();
    // No token: nothing to keep alive (user is on /login). Expired or
    // near-expiry: refresh. (expiresAt - now goes negative once expired,
    // which still satisfies the margin check — intentional.)
    if (!token || !expiresAt) return;
    if (expiresAt - Date.now() < REFRESH_MARGIN_MS) void attemptSessionRestore();
  };

  window.setInterval(tick, CHECK_INTERVAL_MS);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') tick();
  });
}
